import { StructureGroup, StructureGroupConfig } from 'data/generated/graphql';
import { toScreamingSnakeCase } from 'utils/case';
import {
  addModuleToGroup,
  applyModulesToLayout,
  deriveModules,
  EditorLayout,
  FlatSlot,
  removeModuleFromList,
  updateModuleInList
} from 'utils/layoutUtils';

export interface LayoutState {
  layout: EditorLayout;
  lastSavedLayout: EditorLayout;
}

export type LayoutAction =
  | { type: 'update_module'; index: number; module: FlatSlot }
  | { type: 'reorder_modules'; modules: FlatSlot[] }
  | { type: 'add_module'; module: FlatSlot; groupId: string | null; localInsertIndex?: number }
  | { type: 'remove_module'; index: number }
  | { type: 'update_name'; name: string }
  | { type: 'update_content_types'; contentTypes: string[] }
  | { type: 'add_group'; label: string; insertAfterGroupId?: string | null }
  | { type: 'update_group'; groupId: string; patch: Partial<StructureGroupConfig> }
  | { type: 'remove_group'; groupId: string }
  | { type: 'reorder_groups'; groups: StructureGroup[] }
  | { type: 'restore'; layout: EditorLayout }
  | { type: 'publish_success' }
  | { type: 'reset'; layout: EditorLayout };

function applyToModules(state: LayoutState, updater: (m: FlatSlot[]) => FlatSlot[]): LayoutState {
  return {
    ...state,
    layout: applyModulesToLayout(state.layout, updater(deriveModules(state.layout)))
  };
}

export function structureReducer(state: LayoutState, action: LayoutAction): LayoutState {
  switch (action.type) {
    case 'update_module':
      return applyToModules(state, (m) => updateModuleInList(m, action.index, action.module));
    case 'reorder_modules':
      return applyToModules(state, () => action.modules);
    case 'add_module':
      return applyToModules(state, (m) => addModuleToGroup(m, action.module, action.groupId, action.localInsertIndex));
    case 'remove_module':
      return applyToModules(state, (m) => removeModuleFromList(m, action.index));
    case 'update_name':
      return { ...state, layout: { ...state.layout, name: action.name } };
    case 'update_content_types':
      return { ...state, layout: { ...state.layout, contentTypes: action.contentTypes } };
    case 'add_group': {
      const id = toScreamingSnakeCase(action.label.trim());
      const existing = state.layout.structure?.groups ?? [];
      if (existing.some((g) => g.id === id)) return state;
      const newGroup: StructureGroup = {
        id,
        config: { label: action.label, allowedTreatmentTypes: [] },
        modules: []
      };
      let groups: StructureGroup[];
      if (action.insertAfterGroupId === null) {
        groups = [newGroup, ...existing];
      } else {
        const insertIndex = existing.findIndex((g) => g.id === action.insertAfterGroupId);
        groups = [...existing];
        groups.splice(insertIndex !== -1 ? insertIndex + 1 : existing.length, 0, newGroup);
      }
      return { ...state, layout: { ...state.layout, structure: { groups } } };
    }
    case 'update_group':
      return {
        ...state,
        layout: {
          ...state.layout,
          structure: {
            groups: (state.layout.structure?.groups ?? []).map((g) =>
              g.id === action.groupId ? { ...g, config: { ...g.config, ...action.patch } } : g
            )
          }
        }
      };
    case 'remove_group':
      return {
        ...state,
        layout: {
          ...state.layout,
          structure: {
            groups: (state.layout.structure?.groups ?? []).filter((g) => g.id !== action.groupId)
          }
        }
      };
    case 'reorder_groups':
      return { ...state, layout: { ...state.layout, structure: { groups: action.groups } } };
    case 'restore':
      return {
        ...state,
        layout: { ...action.layout, structure: action.layout.structure ?? { groups: [] } }
      };
    case 'publish_success':
      return { ...state, lastSavedLayout: state.layout };
    case 'reset': {
      const normalized = { ...action.layout, structure: action.layout.structure ?? { groups: [] } };
      return { layout: normalized, lastSavedLayout: normalized };
    }
    default:
      return state;
  }
}
